import { useEffect, useRef, useState } from 'react';

//Primereact components
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';

import AppBreadcrumb from '../../../components/CustomeBreadcrumb/AppBreadcrumb';
import { defaultPageRowOptions, defaultRowOptions, SkeletonbodyTemplate, Skeletonitems } from '../../../appconfig/Settings';
import { Toolbar } from 'primereact/toolbar';
import { GoodStandingCertListShowColumns } from '../../../appconfig/DatatableSetting';
import { formatDate } from '../../../appconfig/AppHelper';
import { DOWNLOAD_GOODSTANDING_CERTIFICATE, GET_GOODSTANDING_CERTIFICATE } from '../../../config/ApiConstant';
import { getAllDetails, getAllDetailsDirect } from '../../../service/ProductService';
import moment from 'moment';
import Loader from '../../../components/Loadeer/Loader';

export const GoodStandingCertificateList = () => {
    document.title = 'GoodStanding Certificate List | GSPC';

    //Set Toast/ Filter Properties
    const toast = useRef<any>(null);
    const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
    const [globalFilter, setGlobalFilter] = useState<any>(null);

    // Date Object
    let today = new Date();
    const [dates, setDates] = useState<string | Date | Date[] | any | null>([new Date(today.setDate(today.getDate() - 31)), new Date()]);

    //Loading/Page Loading
    const [pageLoad, setPageLoad] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [certificateList, setCertificateList] = useState<any>([]);

    // useEffect
    useEffect(() => {
        getCertificateList();

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [dates]);

    // Get certificate list
    const getCertificateList = async () => {
        try {
            setPageLoad(false);
            let startDate = moment(dates[0]).format('y-M-D') + ' 00:00:00';
            let endDate = moment(dates[1]).format('y-M-D') + ' 23:59:59';
            const response = await getAllDetailsDirect(GET_GOODSTANDING_CERTIFICATE + '?start_date=' + startDate + '&end_date=' + endDate);
            if (response.list) {
                setPageLoad(true);
                setCertificateList(response.list);
            } else {
                setPageLoad(true);
            }
        } catch (error: any) {
            setPageLoad(true);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Sorry Could Not Load Data'
            });
        }
    };

    // Global search
    const leftToolbarTemplate = () => {
        return (
            <>
                <span className="search-bar mr-3">
                    <i className="pi pi-search"></i>
                    <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
                </span>
            </>
        );
    };

    // On Date Change
    const onDateChange = (e: any) => {
        setDates(e.value);
    };

    //Clear All Filter
    const clearAll = () => {
        setGlobalFieldFilter(null);
        setGlobalFilter(null);
    };

    // Right toolbar
    const rightToolbarTemplate = () => {
        return (
            <>
                <Calendar value={dates} dateFormat="dd/mm/yy" onChange={(e) => onDateChange(e)} selectionMode="range" showIcon className="ml-2" />
                <Button icon="pi pi-filter-slash" label="Clear" onClick={() => clearAll()} className="p-button-outlined ml-2"></Button>
            </>
        );
    };

    // Template for issue date
    const IssueDateTemplate = (rowData: any) => {
        return <>{!window.cn(rowData.CertIDt) ? formatDate(rowData.CertIDt) : ''}</>;
    };

    // Template for country
    const countryTemplate = (rowData: any) => {
        return <>{rowData.USA === 1 ? 'USA' : rowData.CANADA === 1 ? 'CANADA' : 'GREAT BRITAIN'}</>;
    };

    // Template for actions
    const actionsTemplate = (rowData: any) => {
        return (
            <>
                <div className="actions action-btn">
                    <Button icon="pi pi-download" tooltip="Download" tooltipOptions={{ position: 'top' }} className="p-button-primary" onClick={() => downloadCertificate(rowData.SrNo)} />
                </div>
            </>
        );
    };

    // Download certificate
    const downloadCertificate = async (SrNo: any) => {
        try {
            setLoading(true);
            const response = await getAllDetails(DOWNLOAD_GOODSTANDING_CERTIFICATE + '?sr_no=' + SrNo);
            if (response) {
                //PDF Download
                const fileName = `Good-Standing-Certificate-${new Date().getTime()}.pdf`;
                const link = document.createElement('a');
                link.href = response;
                link.target = '_blank';
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                setLoading(false);
            } else {
                setLoading(false);

                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Something Went Wrong' });
            }
        } catch (error: any) {
            if (error.response) {
                setLoading(false);

                //Spinner False and Set Toast
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Something Went Wrong' });
            }
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <AppBreadcrumb currentMenu={'Certificate'} pageName={'GoodStanding Certificate List'} pageTitle={'GoodStanding Certificate List'} goToPage={true} />
            <div className="grid crud-demo">
                <div className="col-12">
                    <div className="card">
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
                        {/* Datatable */}
                        {pageLoad ? (
                            <>
                                <DataTable
                                    value={certificateList}
                                    showGridlines
                                    stripedRows
                                    globalFilter={globalFilter}
                                    filters={globalFieldFilter}
                                    onFilter={(e) => setGlobalFieldFilter(e.filters)}
                                    rows={defaultRowOptions}
                                    paginator={certificateList.length >= 10 && true}
                                    rowsPerPageOptions={defaultPageRowOptions}
                                    className="datatable-responsive p-datatable-sm"
                                    emptyMessage="No Certificates Found"
                                    removableSort
                                >
                                    {GoodStandingCertListShowColumns.map((col, i) => {
                                        if (col.field === 'action') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={actionsTemplate} />;
                                        } else if (col.field === 'country') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={countryTemplate} />;
                                        } else if (col.field === 'CertIDt') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={IssueDateTemplate} />;
                                        } else {
                                            return <Column key={col.field} field={col.field} header={col.header} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                        }
                                    })}
                                </DataTable>
                            </>
                        ) : (
                            <>
                                {/* Skeleton Data table */}
                                <DataTable value={Skeletonitems}>
                                    {GoodStandingCertListShowColumns.map((col, i) => (
                                        <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                    ))}
                                </DataTable>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {loading && <Loader />}
        </>
    );
};
